import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useSession, useIsAuthenticated } from '@/core/auth/SessionStore'
import { PickerSheet } from '@/features/common/PickerSheet'
import {
  useEncarregadosDoProjeto,
  useEquipesDoEncarregado,
  useProjeto,
  useProjetosComContagem,
  useSessoesDoProjeto,
} from '@/core/db/daos'
import { cn } from '@/lib/cn'
import { useProducaoStore } from '@/features/producao/producaoStore'
import { loadSessionItensAsDrafts } from '@/features/producao/producaoRepo'

/**
 * Setup da sessão de produção (replica `SetupScreen.kt`).
 *
 * Workflow:
 *  1. Projeto (puxado da sessão por padrão; pode ser trocado aqui)
 *  2. Encarregado (filtra equipes — UX inversa via `cdt_projeto_tem_equipe`)
 *  3. Equipe (habilitada após encarregado)
 *  4. Toggle "Choveu hoje?"
 *
 * Projeto, Encarregado e Equipe são obrigatórios no app (decisão 2026-05-26 com
 * o EGP). Backend continua aceitando null nessas colunas pra compatibilidade
 * com sessões antigas, mas novos fluxos sempre preenchem os três.
 */
export function ProducaoSetupRoute() {
  const authed = useIsAuthenticated()
  const session = useSession()
  const navigate = useNavigate()

  const store = useProducaoStore()
  const projetos = useProjetosComContagem()
  const projetoSessao = useProjeto(session?.projetoAtualId)
  const encarregados = useEncarregadosDoProjeto(store.projetoId)
  const equipes = useEquipesDoEncarregado(store.projetoId, store.encarregadoId)
  const sessoes = useSessoesDoProjeto(store.projetoId)

  // Bootstrap: se a store ainda não tem projeto, copia do projeto atual da sessão
  useEffect(() => {
    if (store.projetoId == null && projetoSessao != null) {
      store.setProjeto(projetoSessao.id, projetoSessao.codigo)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projetoSessao?.id])

  const [showProjetoPicker, setShowProjetoPicker] = useState(false)
  const [showEncarregadoPicker, setShowEncarregadoPicker] = useState(false)
  const [showEquipePicker, setShowEquipePicker] = useState(false)
  const [loadingExistente, setLoadingExistente] = useState(false)

  if (!authed) return <Navigate to="/login" replace />

  const equipeHabilitada = store.encarregadoId != null
  const semEquipes = equipeHabilitada && (equipes?.length ?? 0) === 0
  const dataFormatada = formatBR(store.dt)

  // Detecta sessão existente que casa com (encarregado+equipe+dt) selecionado.
  const sessaoExistente =
    store.projetoId != null && store.encarregadoId != null
      ? (sessoes ?? []).find(
          (s) =>
            s.projetoId === store.projetoId &&
            s.dt === store.dt &&
            (s.encarregadoId ?? null) === store.encarregadoId &&
            (s.equipeId ?? null) === store.equipeId,
        )
      : undefined

  async function abrirExistente() {
    if (!sessaoExistente) return
    setLoadingExistente(true)
    try {
      const drafts = await loadSessionItensAsDrafts({
        projetoId: sessaoExistente.projetoId,
        dt: sessaoExistente.dt,
        encarregadoId: sessaoExistente.encarregadoId,
        equipeId: sessaoExistente.equipeId,
      })
      store.setItens(drafts)
      navigate('/producao/session')
    } catch (e) {
      console.error('[setup] abrirExistente falhou:', e)
      window.alert(e instanceof Error ? e.message : 'Falha ao carregar sessão')
    } finally {
      setLoadingExistente(false)
    }
  }

  return (
    <main className="min-h-svh bg-bg-base flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border pt-safe pl-safe pr-safe">
        <div className="flex items-center gap-2 px-2 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/producao')} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold text-on-surface">Nova sessão de produção</h1>
        </div>
      </header>

      {/* Form */}
      <div className="flex-1 p-4 space-y-3 max-w-2xl mx-auto w-full">
        <PickerField
          label="PROJETO *"
          value={store.projetoCodigo || 'Selecionar…'}
          onClick={() => setShowProjetoPicker(true)}
        />

        <InfoRow label="DATA" value={dataFormatada} />

        <PickerField
          label="ENCARREGADO *"
          value={store.encarregadoNome ?? 'Selecionar…'}
          onClick={() => setShowEncarregadoPicker(true)}
          disabled={store.projetoId == null}
          hint={
            store.projetoId == null
              ? 'Selecione o projeto primeiro'
              : (encarregados?.length ?? 0) === 0
                ? 'Sem encarregados vinculados a este projeto'
                : undefined
          }
        />

        <PickerField
          label="EQUIPE *"
          value={
            !equipeHabilitada
              ? 'Selecione o encarregado primeiro'
              : semEquipes
                ? 'Sem equipes vinculadas'
                : (store.equipeNome ?? 'Selecionar…')
          }
          onClick={() => setShowEquipePicker(true)}
          disabled={!equipeHabilitada || semEquipes}
        />

        {/* Toggle chuva */}
        <div className="card-surface-variant px-4 py-3 flex items-center gap-3">
          <div className="flex-1">
            <p className="text-on-surface">Choveu hoje?</p>
            <p className="text-xs text-on-surface-dim">
              Aplicado a todos os apontamentos da sessão
            </p>
          </div>
          <Switch checked={store.chuva} onChange={store.setChuva} />
        </div>

        {/* Alerta de duplicata — sessão já existe para mesma equipe+dt */}
        {sessaoExistente && (
          <div className="rounded-xl border border-warn/40 bg-warn/10 p-4 flex gap-3">
            <AlertCircle className="h-5 w-5 text-warn flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-on-surface text-sm">
                Já existe sessão dessa equipe hoje com{' '}
                <strong>{sessaoExistente.total} apontamento(s)</strong>. Continuar abaixo
                criará uma sessão separada.
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void abrirExistente()}
                disabled={loadingExistente}
                className="mt-2"
              >
                {loadingExistente ? 'Abrindo…' : 'Adicionar à sessão existente'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom action */}
      <div className="sticky bottom-0 bg-bg-base/95 backdrop-blur border-t border-border p-4 pb-safe-plus-4 pl-safe pr-safe">
        <Button
          variant="primary"
          size="lg"
          disabled={
            store.projetoId == null ||
            store.encarregadoId == null ||
            store.equipeId == null
          }
          onClick={() => navigate('/producao/session')}
          className="w-full"
        >
          Continuar
        </Button>
      </div>

      {showProjetoPicker && (
        <PickerSheet
          open
          title="Selecionar projeto"
          items={projetos ?? []}
          label={(p) => p.codigo}
          sublabel={(p) =>
            p.qtdServicos > 0 ? `${p.qtdServicos} serviços vinculados` : 'sem serviços cadastrados'
          }
          onPick={(p) => {
            store.setProjeto(p.id, p.codigo)
            setShowProjetoPicker(false)
          }}
          onDismiss={() => setShowProjetoPicker(false)}
          searchHint="Buscar por código"
          emptyMessage="Catálogo de projetos vazio."
        />
      )}

      {showEncarregadoPicker && (
        <PickerSheet
          open
          title="Selecionar encarregado"
          items={[{ id: -1, nome: '— limpar seleção —' }, ...(encarregados ?? [])]}
          label={(e) => e.nome}
          onPick={(e) => {
            if (e.id === -1) store.setEncarregado(null, null)
            else store.setEncarregado(e.id, e.nome)
            setShowEncarregadoPicker(false)
          }}
          onDismiss={() => setShowEncarregadoPicker(false)}
          searchHint="Buscar encarregado"
          emptyMessage="Sem encarregados para este projeto"
        />
      )}

      {showEquipePicker && (
        <PickerSheet
          open
          title="Selecionar equipe"
          items={[{ id: -1, nome: '— limpar seleção —' }, ...(equipes ?? [])]}
          label={(e) => e.nome}
          onPick={(e) => {
            if (e.id === -1) store.setEquipe(null, null)
            else store.setEquipe(e.id, e.nome)
            setShowEquipePicker(false)
          }}
          onDismiss={() => setShowEquipePicker(false)}
          searchHint="Buscar equipe"
          emptyMessage="Sem equipes para este encarregado"
        />
      )}
    </main>
  )
}

// ----------------------------------------------------------------------------
//  Pequenos primitivos UI (mantidos locais — não vale extrair ainda)
// ----------------------------------------------------------------------------

function PickerField({
  label,
  value,
  onClick,
  disabled,
  hint,
}: {
  label: string
  value: string
  onClick: () => void
  disabled?: boolean
  hint?: string
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        'w-full card-surface-variant text-left px-4 py-3',
        'flex items-center gap-3 min-h-touch',
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface-2/80 transition-colors',
      )}
    >
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-on-surface-dim uppercase">{label}</p>
        <p className="text-on-surface mt-0.5 truncate">{value}</p>
        {hint && <p className="text-xs text-on-surface-dim mt-0.5">{hint}</p>}
      </div>
      {!disabled && <ChevronRight className="h-5 w-5 text-on-surface-dim flex-shrink-0" />}
    </button>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="card-surface-variant px-4 py-3">
      <p className="text-xs font-medium text-on-surface-dim uppercase">{label}</p>
      <p className="text-on-surface mt-0.5">{value}</p>
    </div>
  )
}

function Switch({ checked, onChange }: { checked: boolean; onChange: (b: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-7 w-12 items-center rounded-full transition-colors',
        checked ? 'bg-accent' : 'bg-surface-2',
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 transform rounded-full bg-white transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1',
        )}
      />
    </button>
  )
}

function formatBR(isoDate: string): string {
  // isoDate = YYYY-MM-DD; sem fuso porque é só data
  const [y, m, d] = isoDate.split('-')
  return `${d}/${m}/${y}`
}
